import React, { useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Button, Collapse, Stack, Typography } from '@mui/material'
import { com } from '@eidu/form'
import { AddCircleOutline, FileUpload, Launch as LaunchIcon } from '@mui/icons-material'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import exportEntitiesOfType from '../../api/entity/data/exportEntitiesOfType'
import EntityTypeRepository from '../../io/EntityTypeRepository'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import { requireNotUndefinedOrNull } from '../../util/require'
import { ADMIN_SCHEMA_NAME, getTypeDisplayName, isTypeInSchema } from '../../util/entityType/typeNameSchema'
import TanStackDataGrid from '../../components/data/TanStackDataGrid'
import useEntityRepository from '../../io/useEntityRepository'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import EntityTypeNotAccessibleError from './EntityTypeNotAccessibleError'
import EntityRepository from '../../io/EntityRepository'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString

type ListPageContentProps = {
  onImport: () => void
  onNew: () => void
  onDownloadCsv: () => void
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  entityRepository: EntityRepository
  pageSize: number
}

export const ListPageContent = ({
  onImport,
  onNew,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDownloadCsv,
  entityType,
  entityTypes,
  entityRepository,
  pageSize,
}: ListPageContentProps) => {
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {entityType && (
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0 }}>
          <Typography variant="h4">{getTypeDisplayName(entityType.name)}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            {isTypeInSchema(entityType.name, ADMIN_SCHEMA_NAME, 'Navigation') && (
              <Button
                color="primary"
                startIcon={<LaunchIcon />}
                onClick={() => window.open('https://mui.com/material-ui/material-icons/', '_blank', 'noopener')}
              >
                GET ICONS
              </Button>
            )}
            <Button color="primary" startIcon={<FileUpload />} onClick={onImport}>
              IMPORT
            </Button>
            <Button color="primary" startIcon={<AddCircleOutline />} onClick={onNew}>
              ADD
            </Button>
          </Stack>
        </Stack>
      )}
      {entityType && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', pt: 1 }}>
          <TanStackDataGrid
            navigate={navigate}
            entityRepository={entityRepository}
            entityTypes={entityTypes}
            entityType={entityType}
            pageSize={pageSize}
          />
        </Box>
      )}
    </Box>
  )
}

export type ReadyListPageProps = {
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const ReadyListPage = ({ entityType, entityTypes }: ReadyListPageProps) => {
  const navigate = useNavigate()
  const [errorPreparingExport, setErrorPreparingExport] = useState<string | undefined>(undefined)

  const authContext = createAuthContext(useContext<IAuthContext>(AuthContext))

  const entityRepository = useEntityRepository(
    {
      typeId: entityType.id,
      types: entityTypes,
    },
    [entityType.id.toString(), entityTypes.size]
  )

  const prepareCsvDownload = () => {
    exportEntitiesOfType({ id: entityType.id, authContext: requireNotUndefinedOrNull(authContext) })
      .then((jobId) => {
        const args = new URLSearchParams({ jobId }).toString()
        navigate(`/export?${args}`)
      })
      .catch((err) => {
        setErrorPreparingExport(`Failed to prepare export: ${err}`)
      })
  }

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Collapse in={!!errorPreparingExport}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ margin: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <Box>{errorPreparingExport}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      <ListPageContent
        onImport={() => {
          navigate('import')
        }}
        onNew={() => {
          navigate('new')
        }}
        onDownloadCsv={prepareCsvDownload}
        entityType={entityType}
        entityTypes={entityTypes}
        entityRepository={entityRepository}
        pageSize={50}
      />
      <LoadingOverlay isOpen={!entityType} />
    </Stack>
  )
}

export type ListPageProps = {
  entityTypeId: EntityTypeId
  entityTypeRepository: EntityTypeRepository
}

const ListPage = ({ entityTypeId, entityTypeRepository }: ListPageProps) => {
  const entityTypes = entityTypeRepository.useAll()
  const entityType = entityTypeRepository.useItem(entityTypeId)
  const error = entityTypeRepository.useError()?.let((it) => String(it))

  const loadingType = entityTypeRepository.useLoading()
  const loadingOverlayOpen = loadingType && !error

  return (
    <>
      {entityType && entityTypes && (
        <ReadyListPage key={entityType.id.toString()} entityType={entityType} entityTypes={entityTypes} />
      )}
      {(!entityType || !entityTypes) && !loadingOverlayOpen && <EntityTypeNotAccessibleError />}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </>
  )
}

const ListPageRoute = () => {
  const params = useParams()
  const entityTypeRepository = useContext(EntityTypesContext)

  if (params.entityTypeId)
    return (
      <ListPage
        entityTypeId={entityTypeIdFromString(params.entityTypeId)}
        entityTypeRepository={entityTypeRepository}
      />
    )
  else return <Typography sx={{ padding: 3 }}>Unknown state</Typography>
}

export default ListPageRoute
